import {
  createGSplatEffectFromTemplate,
  createSemanticCameraMotion,
  parseGSplatEffectTemplate,
  parseSemanticCameraMotionTemplate,
  type AnimationClipData,
  type AnimationPlayerHandle,
  type AnimationService,
  type GSplatEffectTemplateDiagnostics,
  type GSplatEffectTemplateRuntimeContext,
  type GSplatShaderEffectLike,
  type SemanticCameraMotionContext,
  type SemanticCameraMotionPlan,
  type SemanticCameraMotionResult,
} from '@sharefe/share3d-engine'

const DEFAULT_CAMERA_PLAYER_ID = 'video-render-camera-motion'

export interface Gs3dClipTemplateDetails {
  cameraMotionTemplate: unknown
  cameraClip?: AnimationClipData
  cameraMotionPlan?: SemanticCameraMotionPlan
  effectTemplate: unknown
  durationSeconds: number
  initialCamera: SemanticCameraMotionContext['camera']
  effectContext: GSplatEffectTemplateRuntimeContext
}

export interface Gs3dClipTemplateGsplatTarget {
  setShaderEffect(effect: GSplatShaderEffectLike | null): boolean
  clearShaderEffect?: () => boolean
}

export type Gs3dClipTemplateAnimationService = Pick<
  AnimationService,
  'play' | 'stop' | 'seekAndApply'
>

export interface Gs3dClipTemplateApplierOptions {
  animationService?: Gs3dClipTemplateAnimationService
  gsplat?: Gs3dClipTemplateGsplatTarget
  cameraPlayerId?: string
  restoreInitialCamera?: (camera: SemanticCameraMotionContext['camera']) => void
}

export interface Gs3dClipTemplateApplyResult {
  cameraMotion: SemanticCameraMotionResult
  cameraClip: AnimationClipData
  cameraMotionPlan?: SemanticCameraMotionPlan
  cameraPlayer?: AnimationPlayerHandle
  cameraPlayerId: string
  effect: GSplatShaderEffectLike
  effectDiagnostics: GSplatEffectTemplateDiagnostics
  durationSeconds: number
}

export class Gs3dClipTemplateApplier {
  #current: Gs3dClipTemplateApplyResult | null = null
  #initialCamera: Gs3dClipTemplateDetails['initialCamera'] | null = null
  #activeCameraPlayerId: string | null = null
  #activeCameraPlanSegmentIndex = -1

  constructor(private readonly options: Gs3dClipTemplateApplierOptions = {}) {}

  apply(template: unknown): Gs3dClipTemplateApplyResult {
    this.clear()
    const details = parseGs3dClipTemplateDetails(template)
    const effectTemplate = parseGSplatEffectTemplate(details.effectTemplate)
    const cameraMotionPlan = details.cameraMotionPlan
    const cameraMotion = cameraMotionPlan ?
      createCameraMotionResultFromClip(cameraMotionPlan.segments[0].clip) :
      details.cameraClip ?
        createCameraMotionResultFromClip(details.cameraClip) :
        createSemanticCameraMotion(
          parseSemanticCameraMotionTemplate(details.cameraMotionTemplate),
          { camera: details.initialCamera },
          { duration: details.durationSeconds },
        )
    const effectResult = createGSplatEffectFromTemplate(effectTemplate, details.effectContext)
    const cameraPlayerId = this.options.cameraPlayerId ?? DEFAULT_CAMERA_PLAYER_ID

    let cameraPlayer: Gs3dClipTemplateApplyResult['cameraPlayer']
    if (this.options.animationService) {
      cameraPlayer = this.playCameraClip(
        cameraMotion.clip,
        cameraMotionPlan ? createCameraPlanPlayerId(cameraPlayerId, 0) : cameraPlayerId,
        0,
      )
      this.#activeCameraPlanSegmentIndex = cameraMotionPlan ? 0 : -1
    }

    if (this.options.gsplat && !this.options.gsplat.setShaderEffect(effectResult.effect)) {
      this.stopActiveCameraPlayer(cameraPlayer, cameraPlayerId)
      effectResult.effect.disable()
      this.options.restoreInitialCamera?.(details.initialCamera)
      throw new Error('Gs3dClipTemplateApplier failed to apply GSplat shader effect.')
    }

    this.#initialCamera = details.initialCamera
    this.#current = {
      cameraMotion,
      cameraClip: cameraMotion.clip,
      ...(cameraMotionPlan ? { cameraMotionPlan } : {}),
      ...(cameraPlayer ? { cameraPlayer } : {}),
      cameraPlayerId,
      effect: effectResult.effect,
      effectDiagnostics: effectResult.diagnostics,
      durationSeconds: details.durationSeconds,
    }

    return this.#current
  }

  seekAndApply(timeSeconds: number): void {
    validateNonNegativeFinite(timeSeconds, 'timeSeconds')
    const current = this.#current
    if (!current) {
      return
    }

    if (current.cameraMotionPlan) {
      this.seekCameraMotionPlan(current, timeSeconds)
    } else if (this.options.animationService) {
      this.options.animationService.seekAndApply(
        this.#activeCameraPlayerId ?? current.cameraPlayerId,
        timeSeconds,
      )
    } else {
      current.cameraPlayer?.seekAndApply(timeSeconds)
    }
    current.effect.setElapsedTime?.(timeSeconds)
  }

  clear(): void {
    const current = this.#current
    if (!current) {
      return
    }

    this.stopActiveCameraPlayer(current.cameraPlayer, current.cameraPlayerId)
    if (this.options.gsplat?.clearShaderEffect) {
      this.options.gsplat.clearShaderEffect()
    } else {
      this.options.gsplat?.setShaderEffect(null)
    }
    current.effect.disable()
    this.#current = null

    if (this.#initialCamera) {
      this.options.restoreInitialCamera?.(this.#initialCamera)
      this.#initialCamera = null
    }
  }

  private playCameraClip(
    clip: AnimationClipData,
    playerId: string,
    timeSeconds: number,
  ): AnimationPlayerHandle {
    const service = this.options.animationService
    if (!service) {
      throw new Error('AnimationService is required to play an export camera clip.')
    }
    service.stop(playerId)
    const player = service.play(clip, {
      id: playerId,
      autoplay: false,
    })
    this.#activeCameraPlayerId = playerId
    service.seekAndApply(playerId, timeSeconds)
    return player
  }

  private seekCameraMotionPlan(
    current: Gs3dClipTemplateApplyResult,
    timeSeconds: number,
  ): void {
    const plan = current.cameraMotionPlan
    const service = this.options.animationService
    if (!plan || !service) {
      return
    }

    const globalTime = Math.min(timeSeconds, plan.duration)
    const segmentIndex = findCameraMotionPlanSegmentIndex(plan, globalTime)
    const segment = plan.segments[segmentIndex]
    const localTime = Math.min(
      segment.clip.duration ?? segment.endTime - segment.startTime,
      Math.max(0, globalTime - segment.startTime),
    )

    if (segmentIndex !== this.#activeCameraPlanSegmentIndex) {
      this.stopActiveCameraPlayer(current.cameraPlayer, current.cameraPlayerId)
      current.cameraPlayer = this.playCameraClip(
        segment.clip,
        createCameraPlanPlayerId(current.cameraPlayerId, segmentIndex),
        localTime,
      )
      this.#activeCameraPlanSegmentIndex = segmentIndex
      return
    }

    service.seekAndApply(
      this.#activeCameraPlayerId ?? createCameraPlanPlayerId(current.cameraPlayerId, segmentIndex),
      localTime,
    )
  }

  private stopActiveCameraPlayer(
    cameraPlayer: AnimationPlayerHandle | undefined,
    fallbackPlayerId: string,
  ): void {
    if (this.options.animationService) {
      this.options.animationService.stop(this.#activeCameraPlayerId ?? fallbackPlayerId)
    } else {
      cameraPlayer?.stop()
    }
    this.#activeCameraPlayerId = null
    this.#activeCameraPlanSegmentIndex = -1
  }
}

export function createGs3dClipTemplateApplier(
  options: Gs3dClipTemplateApplierOptions = {},
): Gs3dClipTemplateApplier {
  return new Gs3dClipTemplateApplier(options)
}

function parseGs3dClipTemplateDetails(value: unknown): Gs3dClipTemplateDetails {
  const record = requireRecord(value, 'Clip template must be an object.')
  const durationSeconds = readPositiveFinite(record.durationSeconds, 'durationSeconds')

  if (record.cameraMotionTemplate === undefined) {
    throw new Error('Clip template cameraMotionTemplate is required.')
  }
  if (record.effectTemplate === undefined) {
    throw new Error('Clip template effectTemplate is required.')
  }
  if (record.initialCamera === undefined) {
    throw new Error('Clip template initialCamera is required.')
  }
  if (record.effectContext === undefined) {
    throw new Error('Clip template effectContext is required.')
  }

  return {
    cameraMotionTemplate: record.cameraMotionTemplate,
    ...(record.cameraClip !== undefined ? { cameraClip: record.cameraClip as AnimationClipData } : {}),
    ...(record.cameraMotionPlan !== undefined ?
      { cameraMotionPlan: parseCameraMotionPlan(record.cameraMotionPlan) } :
      {}),
    effectTemplate: record.effectTemplate,
    durationSeconds,
    initialCamera: record.initialCamera as Gs3dClipTemplateDetails['initialCamera'],
    effectContext: record.effectContext as Gs3dClipTemplateDetails['effectContext'],
  }
}

function parseCameraMotionPlan(value: unknown): SemanticCameraMotionPlan {
  const plan = requireRecord(value, 'Clip template cameraMotionPlan must be an object.')
  const duration = readPositiveFinite(plan.duration, 'cameraMotionPlan.duration')
  if (!Array.isArray(plan.segments) || plan.segments.length === 0) {
    throw new Error('Clip template cameraMotionPlan.segments must contain at least one segment.')
  }

  for (const [index, value] of plan.segments.entries()) {
    const segment = requireRecord(
      value,
      `Clip template cameraMotionPlan.segments[${index}] must be an object.`,
    )
    const startTime = readNonNegativeFinite(
      segment.startTime,
      `cameraMotionPlan.segments[${index}].startTime`,
    )
    const endTime = readPositiveFinite(
      segment.endTime,
      `cameraMotionPlan.segments[${index}].endTime`,
    )
    if (endTime <= startTime) {
      throw new Error(
        `Clip template cameraMotionPlan.segments[${index}] must end after it starts.`,
      )
    }
    const clip = requireRecord(
      segment.clip,
      `Clip template cameraMotionPlan.segments[${index}].clip must be an object.`,
    )
    readPositiveFinite(clip.duration, `cameraMotionPlan.segments[${index}].clip.duration`)
  }

  const lastSegment = plan.segments[plan.segments.length - 1] as { endTime: number }
  if (Math.abs(lastSegment.endTime - duration) > 1e-6) {
    throw new Error('Clip template cameraMotionPlan.duration must match its final segment endTime.')
  }
  return value as SemanticCameraMotionPlan
}

function findCameraMotionPlanSegmentIndex(
  plan: SemanticCameraMotionPlan,
  timeSeconds: number,
): number {
  const lastIndex = plan.segments.length - 1
  const index = plan.segments.findIndex((segment, segmentIndex) =>
    timeSeconds < segment.endTime || segmentIndex === lastIndex
  )
  return Math.max(0, index)
}

function createCameraPlanPlayerId(basePlayerId: string, segmentIndex: number): string {
  return `${basePlayerId}-segment-${segmentIndex}`
}

function createCameraMotionResultFromClip(clip: AnimationClipData): SemanticCameraMotionResult {
  return {
    keyframes: [],
    clip,
    diagnostics: {
      distance: 0,
      clamped: false,
    },
  }
}

function requireRecord(value: unknown, message: string): Record<string, unknown> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    throw new Error(message)
  }
  return value as Record<string, unknown>
}

function readPositiveFinite(value: unknown, fieldName: string): number {
  if (typeof value !== 'number' || !Number.isFinite(value) || value <= 0) {
    throw new Error(`Clip template ${fieldName} must be a positive finite number.`)
  }
  return value
}

function readNonNegativeFinite(value: unknown, fieldName: string): number {
  if (typeof value !== 'number' || !Number.isFinite(value) || value < 0) {
    throw new Error(`Clip template ${fieldName} must be a non-negative finite number.`)
  }
  return value
}

function validateNonNegativeFinite(value: number, fieldName: string): void {
  if (!Number.isFinite(value) || value < 0) {
    throw new Error(`Clip template ${fieldName} must be a non-negative finite number.`)
  }
}
